'use strict'

const EventEmitter = require('events')
const NikeTable = require('./NikeTable')

class NikeTableSession extends EventEmitter {
  constructor(address, options) {
    super()

    this._interactionStartTimeout = parseInt(options?.interactionStartTimeout) || 5000
    this._interactionEndTimeout = parseInt(options?.interactionEndTimeout) ||  this._interactionStartTimeout * 2
    this._interactionCheckInterval = parseInt(options?.interactionCheckInterval) || 100
    this._activeStatusTimeout = parseInt(options?.activeStatusTimeout) || 2000
    this._activeNearStatusTimeout = parseInt(options?.activeNearStatusTimeout) || 30000
    this._idleStatusTimeout = parseInt(options?.idleStatusTimeout) || 60000

    this.table = new NikeTable(address, options)
    this.table.on('connect', (data) => this.emit('connect', data))
    this.table.on('disconnect', (data) => this.emit('disconnect', data))
    this.table.on('tag_new', (data) => this.emit('tag_new', data))
    this.table.on('tag_off_table', this._onTagOffTable.bind(this))
    this.table.on('tag_near_table', this._onTagNearTable.bind(this))
    this.table.on('tag_on_hotspot', this._onTagOnHotspot.bind(this))
    this.table.on('orientation_change', this._onOrientationChange.bind(this))

    this._tags = new Map()
    this._activeTags = new Map()
    this._interaction = false
    this._session = false
    this._orientation = this.table.getOrientation()

    this._startInteractionCheckTimer()
  }

  connect() {
    this.table.connect()
  }

  disconnect() {
    this.table.disconnect()
  }

  getStatus() {
    return this.table.getStatus()
  }

  isInteractionStarted() {
    return this._interaction
  }

  isSessionStarted() {
    return this._session
  }

  getOrientation() {
    return this._orientation
  }

  getTag(id) {
    const tag = this._tags.get(id)
    if (tag) {
      return this._getTagData(tag)
    }
    return undefined
  }

  getTags() {
    const tags = []
    this._tags.forEach(tag => {
      tags.push(this._getTagData(tag))
    })
    return tags
  }

  getNewTags() {
    return this.table.getNewTags()
  }

  addWhitelistedTag(tagId) {
    this.table.addWhitelistedTag(tagId)
  }

  removeWhitelistedTag(tagId) {
    this.table.removeWhitelistedTag(tagId)
  }

  _onTagNearTable({id, position}) {
    this._stopInteractionEndTimer()

    const tag = this._getOrCreateTag(id)
    tag.position = position

    switch (tag.status) {
      case 'active':
        const anotherTagOnHotspot =
          this._getAnotherTagOnHotspot(id, tag.side)
        if (anotherTagOnHotspot) {
          this._inactivateTag(tag)
          this._activateTag(anotherTagOnHotspot)
        } else {
          this._startActiveStatusTimer(tag)
        }
        break

      case 'disappear':
        this._makeTagObserved(tag)
        break
    }

    delete tag.side
  }

  _onTagOnHotspot({id, position, side}) {
    this._stopInteractionEndTimer()

    const tag = this._getOrCreateTag(id)
    const previousSide = this._getTagSide(tag)
    tag.position = position
    tag.side = side

    switch (tag.status) {
      case 'observe':
        if (!this._hasAnotherActiveTagOnHotspot(id, side)) {
          this._activateTag(tag)
        }
        break

      case 'active':
      case 'active_near':
      case 'idle':
        if (previousSide === side) {
          this._stopActiveStatusTimer(tag)
          this._stopActiveNearStatusTimer(tag)
          this._stopIdleStatusTimer(tag)
          if (tag.status !== 'active') {
            this._makeTagActive(tag)
          }
        } else {
          if (!this._hasAnotherActiveTagOnHotspot(id, side)) {
            this._inactivateTag(tag)
            this._activateTag(tag)
          }
        }
        break

      case 'disappear':
        if (this._hasAnotherActiveTagOnHotspot(id, side)) {
          this._makeTagObserved(tag)
        } else {
          this._activateTag(tag)
        }
        break
    }
  }

  _onTagOffTable({id, position}) {
    const tag = this._getOrCreateTag(id)
    tag.position = position

    switch (tag.status) {
      case 'observe':
        this._makeTagDisappear(tag)
        break

      case 'active':
        const anotherTagOnHotspot =
          this._getAnotherTagOnHotspot(id, tag.side)
        if (anotherTagOnHotspot) {
          this._inactivateTag(tag)
          this._activateTag(anotherTagOnHotspot)
        } else {
          this._startActiveStatusTimer(tag)
        }
        break
    }

    delete tag.side
  }

  _onOrientationChange(orientation) {
    this._newOrientation = orientation

    if (!this._session && orientation) {
      this._emitOrientationEvent()
    }
  }

  _getOrCreateTag(id) {
    if (!this._tags.has(id)) {
      const tag = {id, status: 'disappear'}
      this._tags.set(id, tag)
      return tag
    }
    return this._tags.get(id)
  }

  _getTagSide(tag) {
    let result = null
    this._activeTags.forEach((value, side) => {
      if (value === tag) {
        result = side
      }
    })
    return result
  }

  _getTagData(tag) {
    const {id, status} = tag
    const side = this._getTagSide(tag)
    return {id, status, side}
  }

  _emitStatusEvent(tag) {
    if (this._interaction) {
      tag = this._getTagData(tag)
      this.emit(`tag_${tag.status}`, tag)
    }
  }

  _emitOrientationEvent() {
    if (this._interaction && this._newOrientation) {
      if (this._orientation !== this._newOrientation) {
        this._orientation = this._newOrientation
        this.emit('orientation_change', this._orientation)
      }
    }
  }

  _makeTagObserved(tag) {
    tag.status = 'observe'
    tag.appearTime = tag.appearTime || Date.now()
    this._emitStatusEvent(tag)
    this._checkIfInteractionStarted()
  }

  _makeTagActive(tag) {
    tag.status = 'active'
    tag.appearTime = tag.appearTime || Date.now()
    this._emitStatusEvent(tag)
    this._checkIfInteractionStarted()
  }

  _makeTagDisappear(tag) {
    delete tag.appearTime
    tag.status = 'disappear'
    this._emitStatusEvent(tag)
    this._checkIfSessionEnded()
    this._checkIfInteractionEnded()
    if (this._allTagsDisappear()) {
      delete this._newOrientation
    }
  }

  _activateTag(tag) {
    this._stopActiveStatusTimer(tag)
    this._stopActiveNearStatusTimer(tag)
    this._stopIdleStatusTimer(tag)
    this._activeTags.delete(this._getTagSide(tag))

    if (this._hasAnotherActiveTagNotOnHotspot(tag.id, tag.side)) {
      const anotherActiveTag = this._activeTags.get(tag.side)
      this._stopActiveStatusTimer(anotherActiveTag)
      this._stopActiveNearStatusTimer(anotherActiveTag)
      this._stopIdleStatusTimer(anotherActiveTag)
      this._inactivateTag(anotherActiveTag)
    }
    this._activeTags.set(tag.side, tag)
    this._makeTagActive(tag)
    this._checkIfSessionStarted()
  }

  _inactivateTag(tag) {
    this._stopActiveStatusTimer(tag)
    this._stopActiveNearStatusTimer(tag)
    this._stopIdleStatusTimer(tag)
    this._activeTags.delete(this._getTagSide(tag))
    if (tag.position === 'off_table') {
      this._makeTagDisappear(tag)
    } else {
      this._makeTagObserved(tag)
    }
  }

  _hasAnotherActiveTagOnHotspot(id, side) {
    const activeTag = this._activeTags.get(side)
    return activeTag !== undefined
      && activeTag.id !== id
      && activeTag.position === 'on_hotspot'
  }

  _hasAnotherActiveTagNotOnHotspot(id, side) {
    const activeTag = this._activeTags.get(side)
    return activeTag !== undefined
      && activeTag.id !== id
      && activeTag.position !== 'on_hotspot'
  }

  _isAnyTagNotOnHotspot() {
    let result = false
    this._tags.forEach(tag => {
      if (tag.position === 'on_hotspot') {
        result = true
      }
    })
    return result
  }

  _getAnotherTagOnHotspot(id, side) {
    let result = undefined
    this._tags.forEach(tag => {
      if (tag.position !== 'on_hotspot') return
      if (tag.side !== side) return
      if (tag.id === id) return
      result = tag
    })
    return result
  }

  _startActiveStatusTimer(tag) {
    if (tag.activeStatusTimer === undefined) {
      tag.activeStatusTimer = setTimeout(() => {
        tag.status = 'active_near'
        this._emitStatusEvent(tag)
        this._startActiveNearStatusTimer(tag)
        delete tag.activeStatusTimer
      }, this._activeStatusTimeout)
    }
  }

  _stopActiveStatusTimer(tag) {
    if (tag.activeStatusTimer) {
      clearTimeout(tag.activeStatusTimer)
      delete tag.activeStatusTimer
    }
  }

  _startActiveNearStatusTimer(tag) {
    if (tag.activeNearStatusTimer === undefined) {
      tag.activeNearStatusTimer = setTimeout(() => {
        tag.status = 'idle'
        this._emitStatusEvent(tag)
        this._startIdleStatusTimer(tag)
        delete tag.activeNearStatusTimer
      }, this._activeNearStatusTimeout)
    }
  }

  _stopActiveNearStatusTimer(tag) {
    if (tag.activeNearStatusTimer) {
      clearTimeout(tag.activeNearStatusTimer)
      delete tag.activeNearStatusTimer
    }
  }

  _startIdleStatusTimer(tag) {
    if (tag.idleStatusTimer === undefined) {
      tag.idleStatusTimer = setTimeout(() => {
        this._inactivateTag(tag)
        this._checkIfSessionEnded()
        delete tag.idleStatusTimer
      }, this._idleStatusTimeout)
    }
  }

  _stopIdleStatusTimer(tag) {
    if (tag.idleStatusTimer) {
      clearTimeout(tag.idleStatusTimer)
      delete tag.idleStatusTimer
    }
  }

  _allTagsDisappear() {
    let allDisappear = true
    this._tags.forEach(tag => {
      if (tag.status !== 'disappear') {
        allDisappear = false
      }
    })
    return allDisappear
  }

  _getTagsMilliseconds() {
    let time = 0
    this._tags.forEach(tag => {
      if (tag.appearTime) {
        time += Date.now() - tag.appearTime
      }
    })
    return time
  }

  _startInteractionCheckTimer() {
    if (this._interactionTimer === undefined) {
      this._interactionTimer = setInterval(
        this._checkIfInteractionStarted.bind(this),
        this._interactionCheckInterval
      )
    }
  }

  _stopInteractionCheckTimer() {
    if (this._interactionTimer) {
      clearInterval(this._interactionTimer)
      delete this._interactionTimer
    }
  }

  _checkIfInteractionStarted() {
    if (!this._interaction) {
      const hasActiveTags = this._activeTags.size > 0
      const noTimeThreshold = this._interactionStartTimeout === 0 && this._tags.size > 0
      const timeThresholdMet = this._getTagsMilliseconds() > this._interactionStartTimeout
      if (hasActiveTags || noTimeThreshold || timeThresholdMet) {
        this._stopInteractionCheckTimer()
        this._interaction = true
        this.emit('interaction_start')

        // Send delayed orientation event
        this._emitOrientationEvent()

        // Send all delayed tags events
        this._tags.forEach(tag => {
          this._emitStatusEvent(tag)
        })

        // Send delayed session event
        if (this._session) {
          this.emit('session_start')
        }
      }
    }
  }

  _checkIfInteractionEnded() {
    if (this._interaction) {
      if (this._allTagsDisappear()) {
        this._startInteractionEndTimer()
      }
    }
  }

  _startInteractionEndTimer() {
    if (this._interactionStopTimer === undefined) {
      this._interactionStopTimer = setTimeout(() => {
        this._tags.clear()
        this._activeTags.clear()
        this._interaction = false
        this.emit('interaction_stop')
        this._startInteractionCheckTimer()
      }, this._interactionEndTimeout)
    }
  }

  _stopInteractionEndTimer() {
    if (this._interactionStopTimer) {
      clearTimeout(this._interactionStopTimer)
      delete this._interactionStopTimer
    }
  }

  _checkIfSessionStarted() {
    if (!this._session) {
      if (this._activeTags.size) {
        this._session = true
        if (this._interaction) {
          this.emit('session_start')
        }
      }
    }
  }

  _checkIfSessionEnded() {
    if (this._session) {
      if (!this._activeTags.size && !this._isAnyTagNotOnHotspot()) {
        this._session = false
        this.emit('session_stop')
        this._emitOrientationEvent()
      }
    }
  }
}

module.exports = NikeTableSession
